import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../components/ButtonMC';
import LoadingMC from '../../components/LoadingMC';
import useCustomerOrdersData from '../../hooks/inbox/data/useCustomerOrdersData';
import useCustomerOrdersUI from '../../hooks/inbox/ui/useCustomerOrdersUI';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';
import { CustomerOrderItemMC } from './components';
import EmptyScreenListInboxMC from '../../components/EmptyScreenListInboxMC';

/**
 * Componente principal CustomerOrdersMC
 *
 * @function CustomerOrdersMC
 * @memberof module:CustomerOrdersMC
 * @param {CustomerOrdersMCProps} props - Props del componente
 * @returns {JSX.Element} Organismo renderizado para gestión de órdenes
 *
 * @example
 * <CustomerOrdersMC customerData={customerData} conversation={conversation} />
 */
const CustomerOrdersMC = memo(({ conversation, customerData }) => {
  const { t } = useTranslation('InboxMC');

  /**
   * Data Layer: carga y paginación de órdenes del cliente
   * @see useCustomerOrdersData
   */
  const {
    orders,
    loading,
    viewMore,
    handleViewMore,
    reloadOrders,
    updateOrderInList
  } = useCustomerOrdersData(customerData);

  /**
   * UI Layer: manejo de modal y acciones visuales
   * @see useCustomerOrdersUI
   */
  const {
    openModalOrder,
    toggleButtonConfig,
  } = useCustomerOrdersUI({
    reloadOrders,
    conversation,
    customerData,
  });

  /**
   * Debug tracking para apertura/cierre del modal y cambios clave
   * @see useTraceUpdate
   */
  useTraceUpdate('CustomerOrdersMC', {
    ordersCount: orders.length,
    loading,
    viewMore,
    customerId: customerData?.customer?.id,
  }, {
    logRenderCount: true,
    logPropChanges: true,
  });

  return (
    <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
      <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
        <LoadingMC loading={loading} />
        <span className="tw-text-sm tw-font-medium tw-text-gray-950">{t('orders.title')}</span>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          {!loading && (
            <ButtonComponent
              outlined
              text={t('orders.createOrder')}
              onClick={() => openModalOrder({ action: 'new' })}
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-my-2"
            />
          )}

          {orders.length === 0 && !loading && (
            <EmptyScreenListInboxMC
              iconName="shopping-bag-outline"
              showDescription={false}
              title={t('orders.noOrders')}
              iconClassName="!tw-size-[24px]"
              containerClassName="tw-w-full"
              innerClassName="!tw-rounded-xl !tw-p-6"
            />
          )}
        </div>

        {orders.map((order) => (
          <div key={`customer-order-${order.id}`} className="tw-w-full tw-mb-4">
            <CustomerOrderItemMC
              order={order}
              openModalOrder={openModalOrder}
              updateOrderInList={updateOrderInList}
            />
          </div>
        ))}

        {viewMore && (
          <div className="tw-flex tw-justify-center tw-mb-2">
            <ButtonComponent
              {...toggleButtonConfig}
              text={t('orders.showMore')}
              icon="arrow-ios-downward-outline"
              onClick={handleViewMore}
            />
          </div>
        )}
      </div>
    </div>
  );
});

CustomerOrdersMC.displayName = 'CustomerOrdersMC';

export default CustomerOrdersMC;